import { Component, inject, OnInit, ViewChild, ElementRef } from '@angular/core';
import { NoteGroup } from '../../../interfaces/note-group.interface';
import { catchError, EMPTY, lastValueFrom, Subject, takeUntil, tap } from 'rxjs';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { NotesApiService } from '../../../services/notes-api.service';
import { MatIconModule } from '@angular/material/icon';
import { CommonModule } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatRadioModule } from '@angular/material/radio';
import { FormsModule } from '@angular/forms';
import { MatInputModule } from '@angular/material/input';
import { Note, NoteAttachment } from '../../../interfaces/note.interface';
import { NoteType } from '../../../interfaces/note-type.interface';
import { downloadFile, downloadUrl } from '../../../utilities/url/download.utilities';
import { NotificationService } from '../../../services/notification.service';

export interface NotesDialogData {
  noteGroup: NoteGroup;
  referenceId: number;
}

@Component({
  selector: 'si-notes-dialog',
  standalone: true,
  imports: [MatDialogModule, MatIconModule, MatButtonModule, MatRadioModule, FormsModule, CommonModule, MatInputModule],
  templateUrl: './notes-dialog.component.html',
  styleUrl: './notes-dialog.component.scss'
})
export class NotesDialogComponent implements OnInit {
  private destroy$ = new Subject<void>();

  dialogReg = inject(MatDialogRef<NotesDialogComponent>);
  data = inject<NotesDialogData>(MAT_DIALOG_DATA);
  notesApiService = inject(NotesApiService);
  notificationService = inject(NotificationService);

  @ViewChild('fileUploadInput') fileUploadInput: ElementRef;

  private readonly allowedFileExtensions: readonly string[] =
    ['.zip', '.pdf', '.eml', '.msg', '.jpg', '.png', '.svg', '.gif', '.txt'];

  readonly allowedExtensions = new Set(this.allowedFileExtensions);
  readonly fileAcceptString = this.allowedFileExtensions.join(',');
  readonly allowedFileTypesText = 'Only the following file types are allowed: ' +
    this.allowedFileExtensions.map(ext => ext.substring(1)).join(', ');

  NoteType = NoteType;
  filterMode: 'all' | 'attachments-only' = 'all';
  notes: Note[] = [];
  allowedLength = 4000;
  newNoteText = '';
  file: File | null = null;
  fileError: string | null = null; 
  latestUploadedFile: File | null = null;
  public showAttachements = true;

  get showAttachmentsOnly(): boolean {
    return this.filterMode === 'attachments-only';
  }

  get filteredNotes(): Note[] {
    if (this.filterMode === 'attachments-only') {
      return this.notes.filter(note => note.attachment);
    }
    return this.notes;
  }

  get submitDisabled(): boolean {
    return this.newNoteText.length === 0 || this.newNoteText.length > this.allowedLength;
  }

  async ngOnInit() {
    this.notes = await lastValueFrom(this.notesApiService.getNotes$(this.data.noteGroup, this.data.referenceId));
  }

  handleAddNote() {
    this.notesApiService.addNote$(this.data.noteGroup, this.data.referenceId, this.newNoteText, this.file).pipe(
      takeUntil(this.destroy$),
      tap((newNote) => {
        this.newNoteText = '';
        if (this.file) {
          this.latestUploadedFile = this.file;
          this.file = null;
          this.fileError = null; 
        }
        this.notes = [newNote, ...this.notes];
        this.notificationService.showNotification({ message: 'Note added successfully' });
      }),
      catchError(() => {
        this.notificationService.showNotification({
          message: 'Error: Could not add note. Please ensure the file type is correct.'
        });
        return EMPTY;
      })
    ).subscribe();
  }

  async handleDownloadAttachment(attachment: NoteAttachment) {
    if (attachment.noteId === -1) {
      downloadFile(this.latestUploadedFile);
    }
    const blob = await lastValueFrom(this.notesApiService.downloadAttachment$(attachment.noteId, attachment.attachmentId));
    const name = attachment.attachmentName;
    downloadUrl(blob, name);
  }

  handleFileChange(files: FileList) {
    if (files && files.length > 0) {
      const file = files[0];
      const extension = file.name.substring(file.name.lastIndexOf('.')).toLowerCase();
      if (this.allowedExtensions.has(extension)) {
        this.file = file;
        this.fileError = null;
      } else {
        this.file = null;
        
        this.fileError = 'Invalid file type. Allowed: zip, pdf, eml, msg, jpg, png, svg, gif, txt.';
        if (this.fileUploadInput) {
            this.fileUploadInput.nativeElement.value = '';
        }
      }
    }
  }
}